import { Component } from 'react';

import axios from 'axios';
import * as platform from 'platform';
import intl from 'react-intl-universal';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import AuthApiInstance from 'api/auth/AuthApi';
import LoginResponse from 'api/auth/responses/LoginResponse';
import ApiError from 'api/common/types/ApiError';
import ErrorCodes from 'constants/ErrorCodes';
import ActionKeysGA from 'constants/ga/ActionKeysGA';
import CategoryKeysGA from 'constants/ga/CategoryKeysGA';
import ModulePaths from 'constants/ModulePaths';
import { getFirstError, getLocalizedErrorString } from 'helpers/ErrorFormat';
import { getGlobalFilters } from 'helpers/GlobalFilterUtils';
import { sendEventGA } from 'helpers/GoogleAnalyticsHelper';
import AuthRouteProps from 'modules/public/PublicRouteProps';
import AuthStorageService from 'services/storage-services/AuthStorageService';
import ModalBackground from 'shared/components/hoc/dummy-background/ModalBackground';
import { LocationStateFrom } from 'shared/types/locationTypes';

import MfaBackupCodeForm from '../../components/mfa-backup-form/MfaBackupForm';
import MfaOtpForm from '../../components/mfa-otp-form/MfaOtpForm';
import MfaSecurityViewState from './MfaSecurityViewState';

class MfaSecurityView extends Component<AuthRouteProps, MfaSecurityViewState> {
  constructor(props: AuthRouteProps) {
    super(props);

    const locationState =
      (props.location.state as Partial<LocationStateFrom> & {
        sessionId: string;
        username: string;
        rememberMe: boolean;
      }) || {};

    this.state = {
      isBackupPage: false,
      sessionId: locationState.sessionId,
      username: locationState.username,
      rememberMe: locationState.rememberMe,
      otp: '',
      backupCode: '',
      trustDevice: false,
    };
  }

  componentWillUnmount(): void {
    this.source.cancel();
  }

  CancelToken = axios.CancelToken;

  source = this.CancelToken.source();

  private togglePage = (isBackupPage: boolean): void => {
    this.setState({ isBackupPage });
  };

  private handleLoginSuccess = async (
    response: LoginResponse
  ): Promise<void> => {
    AuthStorageService.OnLogin(response);
    sendEventGA(CategoryKeysGA.Account, ActionKeysGA.Login);

    const { from } = (this.props.location.state as LocationStateFrom) || {
      from: { pathname: ModulePaths.DashboardPath },
    };

    const globalFiltersSearch = getGlobalFilters(from.search || '');
    await this.props.appContext.onLogin(globalFiltersSearch);
    this.props.history.replace(from);
  };

  private handleError = (error: unknown): void => {
    const { appContext } = this.props;

    if (error instanceof ApiError) {
      const errorCode = getFirstError(error);
      const message =
        errorCode === ErrorCodes.Generic
          ? intl.get('ERR_TOAST_GENERIC_ERROR')
          : intl.get(getLocalizedErrorString(errorCode));
      appContext.setErrorToastText(message);
    } else {
      appContext.setErrorToastText(intl.get('ERR_TOAST_GENERIC_ERROR'));
    }
  };

  private handleOtpVerification = async (): Promise<void> => {
    const { otp, username, rememberMe, sessionId, trustDevice } = this.state;
    const { appContext } = this.props;

    if (!otp) {
      appContext.setErrorToastText(intl.get('ERR_MFA_OTP_REQUIRED_MESSAGE'));
      return;
    }

    try {
      const { os, name: browserName, version: browserVersion } = platform;
      const formattedOs = String(os);
      const browser = `${String(browserName)} ${String(browserVersion)}`;

      const response = await AuthApiInstance.VerifyMfaOtp(
        username,
        otp,
        rememberMe,
        sessionId,
        trustDevice,
        formattedOs,
        browser,
        this.source
      );

      await this.handleLoginSuccess(response);
    } catch (error) {
      this.handleError(error);
    }
  };

  private handleBackupCodeVerification = async (): Promise<void> => {
    const { backupCode, username, rememberMe, sessionId } = this.state;
    const { appContext } = this.props;

    if (!backupCode) {
      appContext.setErrorToastText(intl.get('ERR_MFA_BACKUP_REQUIRED_MESSAGE'));
      return;
    }

    try {
      const response = await AuthApiInstance.VerifyMfaRecoveryCode(
        username,
        backupCode,
        rememberMe,
        sessionId,
        this.source
      );

      await this.handleLoginSuccess(response);
    } catch (error) {
      this.handleError(error);
    }
  };

  private handleFieldChange = <K extends keyof MfaSecurityViewState>(
    key: K,
    value: MfaSecurityViewState[K]
  ): void => {
    this.setState({ [key]: value } as Pick<MfaSecurityViewState, K>);
  };

  private handleTrustDeviceChange = (value: boolean): void => {
    this.setState({ trustDevice: value });
  };

  render(): JSX.Element {
    const { isBackupPage, backupCode, otp, trustDevice } = this.state;
    const { history } = this.props;

    return (
      <ModalBackground>
        <Modal size="md" isOpen backdrop="static" centered>
          <ModalHeader
            className="d-flex align-items-center increase-font"
            toggle={() =>
              history.replace({ pathname: ModulePaths.AuthLoginPath })
            }
          >
            {intl.get('LBL_MFA_SECURITY_TITLE')}
          </ModalHeader>
          <ModalBody>
            {isBackupPage ? (
              <MfaBackupCodeForm
                backupCode={backupCode}
                onBack={() => this.togglePage(false)}
                onBackupCodeVerification={this.handleBackupCodeVerification}
                onBackupCodeChange={(value) =>
                  this.handleFieldChange('backupCode', value)
                }
              />
            ) : (
              <MfaOtpForm
                otp={otp}
                trustDevice={trustDevice}
                onTryOtherOptions={() => this.togglePage(true)}
                onOtpVerification={this.handleOtpVerification}
                onOtpChange={(value) => this.handleFieldChange('otp', value)}
                onTrustDeviceChange={this.handleTrustDeviceChange}
              />
            )}
          </ModalBody>
        </Modal>
      </ModalBackground>
    );
  }
}

export default MfaSecurityView;
